import { useCallback, useEffect, useRef, useState } from 'react';

export const MIN_LOCATION_UPDATE_INTERVAL_SECONDS = 1;
export const MAX_LOCATION_UPDATE_INTERVAL_SECONDS = 30;
export const DEFAULT_LOCATION_UPDATE_INTERVAL_SECONDS = 5;

export type UserLocationStatus = 'idle' | 'requesting' | 'active' | 'denied' | 'unsupported' | 'error';

export interface UserLocation {
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  timestamp: number;
}

export interface UseUserLocationTrackOptions {
  intervalSeconds?: number;
  onLocation?: (location: UserLocation) => void;
}

function clampInterval(intervalSeconds: number): number {
  return Math.min(
    MAX_LOCATION_UPDATE_INTERVAL_SECONDS,
    Math.max(MIN_LOCATION_UPDATE_INTERVAL_SECONDS, intervalSeconds),
  );
}

function userFacingError(error: GeolocationPositionError): string {
  if (error.code === error.PERMISSION_DENIED) {
    return 'Location permission was denied. Enable it in your browser settings to join the twin.';
  }
  if (error.code === error.TIMEOUT) {
    return 'Your location took too long to resolve. Check your GPS signal and try again.';
  }
  return error.message || 'Your location is currently unavailable.';
}

/**
 * Starts an opt-in, high-accuracy browser geolocation watch. The hook keeps
 * the freshest device fix for the UI and throttles outbound location events to
 * the selected interval so callers can safely synchronize it with a backend.
 */
export function useUserLocationTrack({
  intervalSeconds = DEFAULT_LOCATION_UPDATE_INTERVAL_SECONDS,
  onLocation,
}: UseUserLocationTrackOptions = {}) {
  const [status, setStatus] = useState<UserLocationStatus>('idle');
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const lastEmissionRef = useRef<number | null>(null);
  const intervalRef = useRef(clampInterval(intervalSeconds));
  const onLocationRef = useRef(onLocation);

  useEffect(() => {
    intervalRef.current = clampInterval(intervalSeconds);
  }, [intervalSeconds]);

  useEffect(() => {
    onLocationRef.current = onLocation;
  }, [onLocation]);

  const stop = useCallback(() => {
    if (watchIdRef.current !== null && typeof navigator !== 'undefined') {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    lastEmissionRef.current = null;
    setStatus('idle');
    setError(null);
  }, []);

  const start = useCallback(() => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setStatus('unsupported');
      setError('Geolocation is not supported on this device.');
      return;
    }

    if (watchIdRef.current !== null) return;

    setStatus('requesting');
    setError(null);
    lastEmissionRef.current = null;

    const recordPosition = (position: GeolocationPosition) => {
      const nextLocation: UserLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracyMeters: position.coords.accuracy,
        timestamp: position.timestamp,
      };
      setLocation(nextLocation);
      setStatus('active');

      const now = Date.now();
      const intervalMilliseconds = intervalRef.current * 1_000;
      if (
        lastEmissionRef.current === null ||
        now - lastEmissionRef.current >= intervalMilliseconds
      ) {
        lastEmissionRef.current = now;
        onLocationRef.current?.(nextLocation);
      }
    };

    const recordError = (positionError: GeolocationPositionError) => {
      if (positionError.code === positionError.PERMISSION_DENIED) {
        stop();
        setStatus('denied');
        setError(userFacingError(positionError));
        return;
      }

      setError(userFacingError(positionError));
      setStatus((currentStatus) => (currentStatus === 'active' ? currentStatus : 'error'));
    };

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 3_000,
    };

    navigator.geolocation.getCurrentPosition(recordPosition, recordError, options);
    watchIdRef.current = navigator.geolocation.watchPosition(recordPosition, recordError, options);
  }, [stop]);

  useEffect(() => stop, [stop]);

  return {
    status,
    location,
    error,
    intervalSeconds: clampInterval(intervalSeconds),
    isTracking: status === 'requesting' || status === 'active',
    start,
    stop,
  };
}
